'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export type ScheduleGenerationStatus = 'idle' | 'scheduled' | 'running' | 'stopped' | 'timeout' | 'error' | 'completed';

export interface ScheduleGenerationOptions<TInput, TResult> {
  input: TInput;
  /** Include the owner, schedule and route whose results may be replaced. */
  contextKey: string;
  /** Semantic input identity; object/callback churn alone never restarts work. */
  signature: string;
  enabled?: boolean;
  paused?: boolean;
  generate: (input: TInput, options: { signal: AbortSignal; deadlineMs: number }) => Promise<TResult>;
  onCommit: (result: TResult, metadata: { contextKey: string; signature: string; input: TInput }) => void;
  timeoutMs?: number;
  debounceMs?: number;
}

interface Identity { contextKey: string; signature: string }
interface Operation extends Identity {
  lifetime: symbol;
  phase: 'scheduled' | 'running';
  cancel: (reason?: Error) => void;
}
const sameIdentity = (a: Identity, b: Identity) => a.contextKey === b.contextKey && a.signature === b.signature;
const asError = (reason: unknown) => reason instanceof Error ? reason : new Error(String(reason));
const abortError = () => new DOMException('Schedule generation cancelled', 'AbortError');
const timeoutError = () => new DOMException('Schedule generation timed out', 'TimeoutError');

/** Controls lifecycle only. The host keeps its prior results until onCommit wins. */
export function useScheduleGeneration<TInput, TResult>({
  input, contextKey, signature, enabled = true, paused = false, generate, onCommit,
  timeoutMs = 10_000, debounceMs = 300,
}: ScheduleGenerationOptions<TInput, TResult>) {
  const timeout = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : 10_000;
  const debounce = Number.isFinite(debounceMs) && debounceMs >= 0 ? debounceMs : 300;
  const [state, setState] = useState<{ status: ScheduleGenerationStatus; error: Error | null }>({ status: 'idle', error: null });
  const committed = useRef<ScheduleGenerationOptions<TInput, TResult> | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const active = useRef<Operation | null>(null);
  const stopped = useRef<Identity | null>(null);

  // Publication always uses committed callbacks and flags, including a pause
  // or disabled state committed while an older promise is still outstanding.
  useLayoutEffect(() => {
    committed.current = { input, contextKey, signature, enabled, paused, generate, onCommit, timeoutMs: timeout, debounceMs: debounce };
  });

  const begin = useCallback((delay: number) => {
    const options = committed.current;
    const epoch = lifetime.current;
    if (!options || !epoch || !options.enabled || options.paused) return;
    active.current?.cancel();
    const identity: Identity = { contextKey: options.contextKey, signature: options.signature };
    const controller = new AbortController();
    // Each operation owns these timers. Retiring it cannot clear a newer timer.
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    const clearTimers = () => { clearTimeout(debounceTimer); clearTimeout(timeoutTimer); };
    const operation: Operation = {
      ...identity, lifetime: epoch, phase: 'scheduled',
      cancel: (reason = abortError()) => {
        clearTimers();
        if (active.current === operation) active.current = null;
        controller.abort(reason);
      },
    };
    active.current = operation;
    const sameContext = () => lifetime.current === epoch && !!committed.current
      && sameIdentity(committed.current, identity) && committed.current.enabled && !committed.current.paused;
    const isCurrent = () => sameContext() && active.current === operation && !controller.signal.aborted;
    const finish = (status: ScheduleGenerationStatus, error: Error | null, abort = false) => {
      if (!isCurrent()) return;
      clearTimers();
      active.current = null;
      if (abort) controller.abort(error ?? abortError());
      if (!active.current && sameContext()) setState({ status, error });
    };
    const fail = (reason: unknown) => {
      if (!isCurrent()) return;
      const error = asError(reason);
      // A spontaneous provider AbortError is an error. Only explicit stop()
      // publishes stopped; our own retired/aborted operations publish nothing.
      finish(error.name === 'TimeoutError' ? 'timeout' : 'error', error, true);
    };
    const run = () => {
      if (!isCurrent()) return;
      const request = committed.current!;
      const actualInput = request.input;
      const deadlineMs = Date.now() + request.timeoutMs!;
      operation.phase = 'running';
      setState({ status: 'running', error: null });
      timeoutTimer = setTimeout(() => finish('timeout', timeoutError(), true), request.timeoutMs);
      try {
        void request.generate(actualInput, { signal: controller.signal, deadlineMs }).then(result => {
          if (!isCurrent()) return;
          if (Date.now() >= deadlineMs) { finish('timeout', timeoutError(), true); return; }
          committed.current!.onCommit(result, { ...identity, input: actualInput });
          finish('completed', null);
        }).catch(fail);
      } catch (error) {
        fail(error);
      }
    };
    setState({ status: 'scheduled', error: null });
    if (delay === 0) run();
    else debounceTimer = setTimeout(run, delay);
  }, []);

  useLayoutEffect(() => {
    const epoch = Symbol('schedule-generation');
    lifetime.current = epoch;
    const identity = { contextKey, signature };
    if (stopped.current && !sameIdentity(stopped.current, identity)) stopped.current = null;
    if (stopped.current) setState({ status: 'stopped', error: null });
    else if (!enabled || paused) setState({ status: 'idle', error: null });
    else begin(debounce);
    return () => {
      if (lifetime.current !== epoch) return;
      lifetime.current = null;
      // Includes a manually started replacement in this committed lifetime.
      if (active.current?.lifetime === epoch) active.current.cancel();
    };
  }, [begin, contextKey, signature, enabled, paused, timeout, debounce]);

  const start = useCallback(() => {
    const options = committed.current;
    if (!lifetime.current || !options?.enabled || options.paused) return;
    if (active.current?.phase === 'running') return;
    stopped.current = null;
    begin(0);
  }, [begin]);

  const stop = useCallback(() => {
    const options = committed.current;
    const epoch = lifetime.current;
    if (!epoch || !options) return;
    stopped.current = { contextKey: options.contextKey, signature: options.signature };
    active.current?.cancel(abortError());
    if (lifetime.current === epoch && !active.current) setState({ status: 'stopped', error: null });
  }, []);

  return {
    ...state,
    isGenerating: state.status === 'scheduled' || state.status === 'running',
    start,
    stop,
  };
}
